import { useState, type KeyboardEvent } from 'react'

import type { SkillChip } from '@/features/recruiter/api/candidates.api'

interface SkillExperienceInputProps {
  chips: SkillChip[]
  onChange: (chips: SkillChip[]) => void
  suggestions?: string[]
}

/**
 * Skill chips with an inline "min years" number — each chip becomes a
 * `nested` OpenSearch query evaluated against that one skill's own
 * years_of_experience, not any skill on the profile (see
 * CandidateSearchQueryBuilder::addSkillClauses()).
 */
export function SkillExperienceInput({ chips, onChange, suggestions = [] }: SkillExperienceInputProps) {
  const [draft, setDraft] = useState('')

  const addChip = (value: string) => {
    const trimmed = value.trim()
    if (trimmed && !chips.some((c) => c.name === trimmed)) {
      onChange([...chips, { name: trimmed }])
    }
    setDraft('')
  }

  const removeChip = (name: string) => onChange(chips.filter((c) => c.name !== name))

  const setMinYears = (name: string, minYears: string) => {
    const parsed = minYears === '' ? undefined : Number(minYears)
    onChange(chips.map((c) => (c.name === name ? { ...c, min_years: parsed } : c)))
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addChip(draft)
    }
  }

  const visibleSuggestions = suggestions.filter((s) => !chips.some((c) => c.name === s)).slice(0, 10)

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {chips.map((chip) => (
          <span key={chip.name} className="badge bg-primary-transparent d-flex align-items-center gap-2 py-1 px-2">
            {chip.name}
            <input
              type="number"
              min={0}
              step={0.5}
              className="form-control form-control-sm py-0"
              style={{ width: 60, display: 'inline-block' }}
              placeholder="Yrs"
              aria-label={`Minimum years of experience in ${chip.name}`}
              value={chip.min_years ?? ''}
              onChange={(e) => setMinYears(chip.name, e.target.value)}
            />
            <button
              type="button"
              className="btn-close fs-10"
              aria-label={`Remove ${chip.name}`}
              onClick={() => removeChip(chip.name)}
            />
          </span>
        ))}
      </div>
      <input
        type="text"
        className="form-control"
        placeholder="e.g. Laravel — press Enter to add"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => addChip(draft)}
      />
      {visibleSuggestions.length > 0 && (
        <div className="mt-2 d-flex flex-wrap gap-1">
          {visibleSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="btn btn-sm btn-light border rounded-pill fs-12 py-1 px-2"
              onClick={() => addChip(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
